/* eslint-disable jsx-a11y/anchor-is-valid */
import clsx from 'clsx'
import {FC} from 'react'
import {KTIcon} from '../../../../helpers'
import {useAuth, type TNotification} from '../../../../../app/modules/auth'
import {dateMask} from '../../../../../utils/dateMask'
import {cleanNotifications} from './core/_request'
import Swal from 'sweetalert2'
import {useNavigate} from 'react-router'
import {chooseUserFranchise} from '../../../../../pages/chooseFranchise/core/_request'
import {MenuComponent} from '../../../../assets/ts/components'

interface Props {
  notifications: TNotification[] | undefined
}

const HeaderNotificationsMenuNav: FC<Props> = ({notifications}: Props) => {
  const navigate = useNavigate()
  const {currentFranchise, setCurrentUser} = useAuth()

  const removeNotificationFromState = (notificationId: string) => {
    setCurrentUser((prev) => {
      if (!prev) return prev
      return {
        ...prev,
        unreadNotifications: (prev.unreadNotifications ?? []).filter(
          (n) => n.id !== notificationId
        ),
      }
    })
    MenuComponent.hideDropdowns(undefined)
  }

  const handleMarkAsRead = async () => {
    await Swal.fire({
      title: 'Deseja limpar as notificações ?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Confirmar',
      cancelButtonText: 'Cancelar',
      customClass: {
        confirmButton: 'btn btn-danger', // Classe personalizada para o botão de confirmação
        cancelButton: 'btn btn-secondary', // Classe personalizada para o botão de cancelamento (opcional)
      },
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await cleanNotifications()
          await Swal.fire({
            position: 'center',
            icon: 'success',
            title: 'Sucesso',
            showConfirmButton: false,
            timer: 1500,
          })
          window.location.reload()
        } catch (error: any) {
          if (error.response.status === 422) {
            let errorText = ''
            for (let key in error.response.data.errors) {
              errorText += '<li>' + error.response.data.errors[key][0] + '</li>'
            }
            await Swal.fire({
              icon: 'error',
              title: 'Ops! Revise seu formulário e tente novamente!',
              html: errorText,
            })
          } else {
            await Swal.fire({
              icon: 'error',
              title: 'Oops...',
              text: error.response.data.message,
              timer: 2000,
            })
          }
        }
      }
    })
  }

  const handleNavigate = async (alert: any, alertType: string) => {
    if (currentFranchise?.id !== alert.franchise_id) {
      await Swal.fire({
        title: 'Você está na franquia incorreta. Vamos redirecioná-lo.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Confirmar',
        cancelButtonText: 'Cancelar',
        customClass: {
          confirmButton: 'btn btn-danger', // Classe personalizada para o botão de confirmação
          cancelButton: 'btn btn-secondary', // Classe personalizada para o botão de cancelamento (opcional)
        },
      }).then(async (result) => {
        if (result.isConfirmed) {
          try {
            await chooseUserFranchise({franchise: alert.franchise_cnpj})

            await Swal.fire({
              position: 'center',
              icon: 'success',
              title: 'Sucesso',
              showConfirmButton: false,
              timer: 1500,
            })

            if (alert.id) {
              await cleanNotifications(alert.id).catch(() => undefined)
            }

            if (alertType === 'SaleUnderReview' || alertType === 'SaleReviewed') {
              window.location.href = `/sales/list/${alert.sale_id}`
              return
            }

            if (alertType === 'BudgetUnderReview' || alertType === 'BudgetReviewed') {
              window.location.href = `/sales/create?budget=${alert.budget_id}`
              return
            }

            if (alertType === 'TransferGenerated') {
              window.location.href = `/transfers/list/${alert.transfer_id}`
              return
            }
          } catch (error: any) {
            if (error.response.status === 422) {
              let errorText = ''
              for (let key in error.response.data.errors) {
                errorText += '<li>' + error.response.data.errors[key][0] + '</li>'
              }
              await Swal.fire({
                icon: 'error',
                title: 'Ops! Revise seu formulário e tente novamente!',
                html: errorText,
              })
            } else {
              await Swal.fire({
                icon: 'error',
                title: 'Oops...',
                text: error.response.data.message,
                timer: 2000,
              })
            }
          }
        }
      })
    } else {
      if (alert.id) {
        await cleanNotifications(alert.id).catch(() => undefined)
        removeNotificationFromState(alert.id)
      }

      if (alertType === 'SaleUnderReview' || alertType === 'SaleReviewed') {
        navigate(`/sales/list/${alert.sale_id}`)
        return
      }

      if (alertType === 'BudgetUnderReview' || alertType === 'BudgetReviewed') {
        navigate(`/sales/create?budget=${alert.budget_id}`)
        return
      }

      if (alertType === 'TransferGenerated') {
        navigate(`/transfers/list/${alert.transfer_id}`)
        return
      }
    }
  }
  return (
    <div className='menu menu-sub menu-sub-dropdown menu-column  w-lg-400px' data-kt-menu='true'>
      {notifications !== undefined && notifications.length > 0 ? (
        <>
          <div className='d-flex flex-column rounded-top' style={{backgroundColor: '#f1116c'}}>
            <h3 className='text-white fw-bold px-9 mt-10 mb-6'>
              Notificações <span className='fs-7 opacity-75 ps-3'>{notifications.length}</span>
            </h3>
            <button className='btn btn-sm btn-danger' onClick={() => handleMarkAsRead()}>
              Limpar Notificações
            </button>
          </div>

          <div className='tab-content'>
            <div className='scroll-y mh-325px my-5 px-8'>
              {notifications.map((alert, index) => {
                const alertType = alert?.type?.split('\\').pop() ?? ''
                const isReviewed = alertType === 'SaleReviewed' || alertType === 'BudgetReviewed'
                const reviewApproved = isReviewed && alert?.approved
                const reviewRejected = isReviewed && !alert?.approved
                return (
                  <div key={`alert${index}`} className='d-flex flex-stack py-4'>
                    <div className='d-flex align-items-center'>
                      <div className='symbol symbol-35px me-4'>
                        <span
                          className={clsx('symbol-label', {
                            'bg-light-success': reviewApproved,
                            'bg-light-danger': reviewRejected,
                            'bg-light-primary': !isReviewed,
                          })}
                        >
                          <KTIcon
                            iconName={
                              reviewApproved
                                ? 'check-circle'
                                : reviewRejected
                                ? 'cross-circle'
                                : 'information-5'
                            }
                            className={clsx('fs-2', {
                              'text-success': reviewApproved,
                              'text-danger': reviewRejected,
                              'text-primary': !isReviewed,
                            })}
                          />
                        </span>
                      </div>

                      <div className='mb-0 me-2'>
                        <div className='d-flex align-items-center justify-content-between'>
                          <span
                            onClick={() => handleNavigate(alert, alertType)}
                            className='fs-6 text-gray-800 text-hover-danger fw-bolder me-3'
                            style={{cursor: 'pointer'}}
                          >
                            {alertType === 'SaleUnderReview'
                              ? 'Venda aguardando aprovação'
                              : alertType === 'SaleReviewed'
                              ? alert?.approved
                                ? 'Venda aprovada'
                                : 'Venda reprovada'
                              : alertType === 'BudgetUnderReview'
                              ? 'Orçamento aguardando aprovação'
                              : alertType === 'BudgetReviewed'
                              ? alert?.approved
                                ? 'Orçamento aprovado'
                                : 'Orçamento reprovado'
                              : 'Transferência aguardando aprovação'}
                          </span>
                        </div>

                        {alertType === 'SaleUnderReview' && (
                          <div className='text-gray-400 fs-6 mt-2'>
                            {'Nº venda -> '}
                            {alert?.sale_id ?? '---'}
                            {alert?.franchise_name ? ` - ${alert.franchise_name}` : '---'}
                          </div>
                        )}
                        {alertType === 'SaleReviewed' && (
                          <div className='text-gray-400 fs-6 mt-2'>
                            {'Nº venda -> '}
                            {alert?.sale_id ?? '---'}
                            {alert?.franchise_name ? ` - ${alert.franchise_name}` : '---'}
                            {alert?.observation ? ` - ${alert.observation}` : ''}
                          </div>
                        )}
                        {alertType === 'BudgetUnderReview' && (
                          <div className='text-gray-400 fs-6 mt-2'>
                            {'Nº orçamento -> '}
                            {alert?.budget_id ?? '---'}
                            {alert?.franchise_name ? ` - ${alert.franchise_name}` : '---'}
                          </div>
                        )}
                        {alertType === 'BudgetReviewed' && (
                          <div className='text-gray-400 fs-6 mt-2'>
                            {'Nº orçamento -> '}
                            {alert?.budget_id ?? '---'}
                            {alert?.franchise_name ? ` - ${alert.franchise_name}` : '---'}
                            {alert?.observation ? ` - ${alert.observation}` : ''}
                          </div>
                        )}
                        {alertType === 'TransferGenerated' && (
                          <div className='text-gray-400 fs-6 mt-2'>
                            {alert?.origin_franchise_name} {'->'}{' '}
                            {alert?.destination_franchise_name ?? '---'}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        </>
      ) : (
        <>
          <div className='d-flex flex-column rounded-top' style={{backgroundColor: '#f1416c'}}>
            <h3 className='text-white fw-bold px-9 mt-10 mb-6'>
              Notificações <span className='fs-8 opacity-75 ps-3'>0</span>
            </h3>
          </div>

          <div className='tab-content'>
            <div className='scroll-y mh-325px my-5 px-8'>
              <h1>Nenhuma notificação encontrada!!</h1>
            </div>
          </div>
        </>
      )}
    </div>
  )
}

export {HeaderNotificationsMenuNav}
